import React, { useRef } from 'react';
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Box from '@mui/material/Box';
import Input from '@mui/material/Input';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import styled from "styled-components";
import { InputAdornment, debounce } from '@mui/material';

import AddEditProductDialog from '../../components/dialogs/AddEditProductDialog';
import Btn from "../../components/Btn";
import CategoryDialog from "../../components/dialogs/CategoryDialog";
import Icn from '../../components/Icn';
import ImportExcelDialog from "../../components/dialogs/ImportExcelDialog";
import Product from "../../components/Product";
import CategoryTabs from '../../components/CategoryTabs';
import Tx from "../../components/Tx";
import useZibi from "../../zibi/UseZibi";
import { permissionsMap } from "../../maps/permissionsMap";
import { addOrDeleteQueryParams, getQueryValue } from "../../domain/queryHandler";
import { colors } from "../../maps/colors";
import { api } from "../../maps/data.js";
import useInitialRender from '../../components/hooks/useInitialRender';

const ProductsListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 10px;
  gap: 12px;
`;

const INITIAL_PAGE = 0;

const getCategories = async () => {
    const categories_res = await api({ type: 'getCategories' });
    return categories_res;
};

export default function ProductPage(props) {
    const history = useNavigate();
    const location = useLocation();
    const initialRender = useInitialRender();

    const searchInputRef = useRef();
    const { onSelect, readonly, selectedProducts } = props;

    const [open_product_dialog, s_open_product_dialog] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [openExcelDialog, setOpenExcelDialog] = useState(false);
    const [category_item_for_edit, s_category_item_for_edit] = useState({});
    const [open_category_dialog, s_open_category_dialog] = useState(false);
    const [products, s_products] = useState([]);
    const [categories, s_categories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState({});
    const [currentPage, setCurrentPage] = useState(0);
    const [callingTheApi, setCallingTheApi] = useState(false);
    const [hasNextPage, setHasNextPage] = useState(true);
    const [total, setTotal] = useState(0);
    const [user] = useZibi('user');
    const scrollRef = useRef();

    const userRole = user?.role?.[0];
    const permissions = permissionsMap[userRole];

    const debouncedOnChange = useRef(debounce(async (e) => {
        scrollRef.current.scrollTo(0, 0);
        const searchQuery = e.target.value;
        setSearchQuery(searchQuery);
        setCurrentPage(INITIAL_PAGE);
    }, 300)).current;

    const [sentryRef] = useInfiniteScroll({
        callingTheApi,
        hasNextPage,
        onLoadMore: () => getProducts(selectedCategory, currentPage, searchQuery)
    });

    const changeUrl = (query) => {
        history({ search: query });
    }

    const clearSearchInput = () => {
        setSearchQuery('')
        searchInputRef.current.value = '';
    }

    const handleAddCategory = () => {
        // Add new category button
        s_category_item_for_edit({});
        s_open_category_dialog(true);
    }

    const changeCategory = (selectedCategory) => {
        searchInputRef.current && clearSearchInput();
        setSelectedCategory(selectedCategory);

        const urlToPush = addOrDeleteQueryParams(location, [
            { categoryId: selectedCategory._id }
        ]);

        changeUrl(urlToPush);
        getProducts(selectedCategory, INITIAL_PAGE, '');
        setCurrentPage(INITIAL_PAGE);
    };

    async function getProducts(category, page, searchQuery = '') {
        if (callingTheApi) return;
        setCallingTheApi(true);

        const categoryId = searchQuery ? null : category?._id;
        let productsPage = currentPage;

        if (Number.isInteger(page)) {
            productsPage = page;
        }

        const { products, total } = await api({
            type: 'getProducts',
            params: `?search=${searchQuery}`,
            data: { page: productsPage, categoryId }
        });

        if (page === 0) {
            s_products(products);
            setHasNextPage(true);
        } else {
            s_products(prev => [...prev, ...products]);
            if (products.length < 25) {
                setHasNextPage(false);
            }
        }

        setTotal(total);
        setCurrentPage(currPage => currPage + 1);
        setCallingTheApi(false);
    }

    const handleEdit = (category) => {
        s_category_item_for_edit(category);
        s_open_category_dialog(true);
    }

    const handleDelete = async (category) => {
        window.messageBox(`Are you sure delete category "${category.name}"?`, async (yesOrNo) => {
            if (yesOrNo) {
                await api({ type: 'removeCategory', data: { categoryId: category._id } });
                const categories = await refreshCategories();
                if (categories.length > 0) {
                    const initialCategory = categories[0];
                    setSelectedCategory(initialCategory);
                    getProducts(initialCategory, INITIAL_PAGE, searchQuery);
                }
            }
        }, true);
    }

    const refreshCategories = async () => {
        const categories = await getCategories();
        s_categories(categories);
        return categories;
    }

    const getMetadata = async () => {
        const categories = await refreshCategories();
        const categoryIdFromQuery = getQueryValue(location, 'categoryId');

        if (categories.length > 0) {
            const initialCategory = categoryIdFromQuery
                ? categories.find(category => category._id === categoryIdFromQuery)
                : categories[0];
            setSelectedCategory(initialCategory);
            getProducts(initialCategory, INITIAL_PAGE, searchQuery);
        }
    }

    useEffect(() => {
        if (!initialRender) {
            const categoryIdFromQuery = getQueryValue(location, 'categoryId');
            const category = searchQuery
                ? {}
                : categories.find(category => category._id === categoryIdFromQuery)
                || categories[0]
            setSelectedCategory(category)
            getProducts(category, INITIAL_PAGE, searchQuery);
        }
    }, [searchQuery]);

    // Initial
    useEffect(() => { getMetadata() }, []);

    const showActions = permissions?.productListActions?.allow;
    const productActiveController = permissions?.productActiveController;

    return (
        <div style={{ height: '100%' }}>
            {/* Page Content */}
            <Box sx={{ flexGrow: 1, height: '100%', bgcolor: 'background.paper', display: 'flex' }}>
                <CategoryTabs
                    readonly={userRole !== 'SUPER_ADMIN' || readonly}
                    selectedCategory={selectedCategory}
                    onChange={changeCategory}
                    onEdit={handleEdit}
                    onAddCategory={handleAddCategory}
                    onDelete={handleDelete}
                    categories={categories}
                    setCategories={s_categories}
                    isLoading={callingTheApi}
                    hasProducts={products.length > 0}
                />

                <div ref={scrollRef} style={{ width: '100%', marginTop: 5, overflowY: 'scroll' }}>
                    <div style={{ display: 'flex', alignItems: 'center', position: 'sticky', top: 0, zIndex: 999, background: colors.WHITE }}>
                        {showActions && !readonly && (
                            <>
                                <Btn startIcon="add" endIcon="product" style={{ margin: 10 }} onClick={() => {
                                    s_open_product_dialog(true);
                                }}><Tx>product.add</Tx></Btn>

                                <Btn startIcon="add" style={{ margin: 10 }} onClick={() => {
                                    setOpenExcelDialog(true);
                                }}><Tx>g.importFromExcel</Tx></Btn>
                                <span style={{ fontSize: 18 }}>
                                    <Tx>product.totalProducts</Tx>: <span style={{ fontWeight: 'bold' }}>{total}</span>
                                </span>
                            </>
                        )}

                        {/* Search Input */}
                        <span style={{ display: 'flex', flex: 1, justifyContent: 'end', padding: 16 }}>
                            <Input
                                inputRef={searchInputRef}
                                onChange={debouncedOnChange}
                                startAdornment={<InputAdornment position="start"><Icn>glassDetails</Icn></InputAdornment>}
                                placeholder='Search...'
                            />
                        </span>
                    </div>

                    <ProductsListWrapper>
                        {products.map((product) => <div key={product._id}>
                            <Product
                                onSelectProduct={onSelect}
                                isSelected={selectedProducts?.some(p => p._id === product._id)}
                                productActiveController={productActiveController}
                                disabled={userRole === 'SITE_ADMIN' && !product?.clientProduct?.data?.active}
                                isActive={userRole === 'SITE_ADMIN' ? product?.siteProduct?.data?.active : product?.clientProduct?.data?.active}
                                readonly={readonly}
                                {...product}

                                onRemoveProduct={(product) => {
                                    if (product) {
                                        s_products(products.filter(pro => pro._id !== product._id));
                                        setTotal(currentTotal => currentTotal - 1)
                                    }
                                }}
                                onNewProduct={(newProduct) => {
                                    const newProductsList = products.map(product => {
                                        if (product._id === newProduct._id) {
                                            return newProduct;
                                        }
                                        return product;
                                    });
                                    s_products(newProductsList);
                                }}
                                onClose={(newProduct) => {
                                    s_open_product_dialog(false);
                                }}
                            />
                            <div style={{ position: 'relative', top: '-25vh' }} ref={sentryRef}></div>
                        </div>
                        )}
                    </ProductsListWrapper>
                </div>
                {
                    open_category_dialog && <CategoryDialog onClose={() => {
                        refreshCategories();
                        s_category_item_for_edit({});
                        s_open_category_dialog(false);
                    }} open={open_category_dialog} category={category_item_for_edit} />
                }
                {
                    open_product_dialog && <AddEditProductDialog
                        categoryId={selectedCategory._id} open={open_product_dialog}
                        product={false}
                        onClose={() => {
                            s_open_product_dialog(false);
                        }}
                        onNewProduct={(newProduct, isNew) => {
                            if (newProduct.categoryId === selectedCategory._id) {
                                const productsTemp = [...products];
                                if (isNew) {
                                    productsTemp.unshift(newProduct);
                                } else {
                                    productsTemp.forEach(pro => {
                                        if (pro._id === newProduct._id) {
                                            pro = newProduct;
                                        }
                                    });
                                }
                                s_products(productsTemp);
                            }
                        }} />
                }

                {openExcelDialog && (
                    <ImportExcelDialog
                        open={openExcelDialog}
                        categories={categories}
                        onClose={() => setOpenExcelDialog(false)}
                        refresh={() => getProducts(selectedCategory, INITIAL_PAGE, searchQuery)}
                    />
                )}
            </Box>
        </div>
    );
}